import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { ArrowLeft, CheckCircle, Eye, EyeOff } from "lucide-react";
import { showError, showErrorWithApiError, showSuccess } from "@/shared/utils/toast-utils";
import { ERROR, SUCCESS } from "@/config/toast-message-config";
import { useResetPassword, useValidateToken } from "@/app/auth/services/auth";
import { validatePassword, getPasswordStrengthColor, getPasswordStrengthLabel } from "@/shared/utils/password-validator";
import orbiusLogo from "@/assets/orbius_logo.png";
import launchBetterImage from "@/assets/Launch_better.jpg";
import { ROUTES } from "@/config/routeConfig";

const enum ResetPasswordState {
  VALIDATING_TOKEN,
  INVALID_RESET_LINK,
  RESET_PASSWORD,
  PASSWORD_RESET_SUCCESS
}

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const resetPassword = useResetPassword();
  const validateToken = useValidateToken();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const token = useRef<string | null>(null);
  const validatedTokenRef = useRef<string | null>(null);
  const [resetPasswordState, setResetPasswordState] = useState<ResetPasswordState>(ResetPasswordState.VALIDATING_TOKEN);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam) {
      showError(ERROR.PASSWORD.MISSING_TOKEN);
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    // Guard against re-validating the same token (e.g. re-renders triggered by
    // the mutation's state changes or React StrictMode double-invoke).
    if (validatedTokenRef.current === tokenParam) {
      return;
    }
    validatedTokenRef.current = tokenParam;
    token.current = tokenParam;

    const validateTokenFn = async (tokenToValidate: string) => {
      try {
        await validateToken.mutateAsync(tokenToValidate);
        setResetPasswordState(ResetPasswordState.RESET_PASSWORD);
      } catch (error) {
        showErrorWithApiError(error, ERROR.PASSWORD.INVALID_TOKEN);
        setResetPasswordState(ResetPasswordState.INVALID_RESET_LINK);
        setTimeout(() => {
          navigate(ROUTES.LOGIN, { replace: true });
        }, 2000);
      }
    };

    void validateTokenFn(tokenParam);
  }, [searchParams, navigate, validateToken]);

  const passwordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      showError(ERROR.PASSWORD.INVALID_TOKEN);
      return;
    }

    if (!passwordValidation.isValid) {
      // Show specific error message for easily guessable passwords
      if (!passwordValidation.isNotEasilyGuessable) {
        showError(ERROR.PASSWORD.EASYLY_GUESSABLE_PASSWORD);
      } else {
        showError(ERROR.PASSWORD.WEAK_PASSWORD);
      }
      return;
    }

    if (!passwordsMatch) {
      showError(ERROR.PASSWORD.PASSWORDS_DONT_MATCH);
      return;
    }

    try {
      await resetPassword.mutateAsync({
        token: token.current,
        newPassword: password
      });
      setResetPasswordState(ResetPasswordState.PASSWORD_RESET_SUCCESS);
      showSuccess(SUCCESS.PASSWORD.RESET_SUCCESS);
    } catch (error) {
      showError(ERROR.PASSWORD.RESET_FAILED);
    }
  };

  return (
    <>
      <div className="relative min-h-screen overflow-hidden bg-muted">
        <img
          src={launchBetterImage}
          alt="Launch Better"
          className="pointer-events-none absolute inset-0 h-full w-full object-cover object-right -scale-x-100"
        />
        <div className="relative z-10 flex min-h-screen w-full flex-col p-5 gap-5">
          <div className="flex w-full items-center justify-between">
            <img src={orbiusLogo} alt="Orbius Logo" className="h-16 w-auto drop-shadow-md" />
            <Button
              variant="outline"
              onClick={() => window.open('mailto:support@orbius.com', '_blank')}
            >
              Contact us
            </Button>
          </div>
          <div className="grid grid-cols-2 flex-1 items-center justify-center">
            <div className="w-full h-full">
            </div>
            <div className="flex items-center justify-center w-full h-full">
              {resetPasswordStateBody()}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  //Reset Password State
  function resetPasswordStateBody() {
    return <>
      {resetPasswordState === ResetPasswordState.VALIDATING_TOKEN && validatingTokenBody()}
      {resetPasswordState === ResetPasswordState.INVALID_RESET_LINK && invalidResetLinkBody()}
      {resetPasswordState === ResetPasswordState.RESET_PASSWORD && resetPasswordBody()}
      {resetPasswordState === ResetPasswordState.PASSWORD_RESET_SUCCESS && passwordResetSuccessBody()}
    </>
  }

  //Validating Token
  function validatingTokenBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader>
          <CardTitle>
            Validating Reset Token
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
          <p>
            Please wait while we validate your password reset link...
          </p>
        </CardContent>
      </Card>
    );
  }

  //Invalid Reset Link
  function invalidResetLinkBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader>
          <CardTitle>
            Invalid Reset Link
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p>
            This password reset link is invalid or has expired. You will be redirected to the login page shortly.
          </p>
          <div className="pt-4">
            <Link to={ROUTES.LOGIN}>
              <Button className="w-full bg-primary hover:bg-primary/90">
                Go to Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  //Reset Password
  function resetPasswordBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader>
          <CardTitle>
            Reset Your Password
          </CardTitle>
          <CardDescription>
            Enter your new password to reset your password.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                New Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Password Requirements */}
              {password && (
                <div className="text-xs space-y-1 mt-2">
                  <div className={`flex items-center ${passwordValidation.minLength ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.minLength ? '✓' : '✗'}</span>
                    At least 12 characters
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasUpperCase ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.hasUpperCase ? '✓' : '✗'}</span>
                    One uppercase letter
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasLowerCase ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.hasLowerCase ? '✓' : '✗'}</span>
                    One lowercase letter
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasNumbers ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.hasNumbers ? '✓' : '✗'}</span>
                    One number
                  </div>
                  <div className={`flex items-center ${passwordValidation.hasSpecialChar ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.hasSpecialChar ? '✓' : '✗'}</span>
                    One special character
                  </div>
                  <div className={`flex items-center ${passwordValidation.isNotEasilyGuessable ? 'text-green-500' : 'text-red-500'}`}>
                    <span className="mr-1">{passwordValidation.isNotEasilyGuessable ? '✓' : '✗'}</span>
                    Not easily guessable (no month/year, common passwords, or sequential patterns)
                  </div>
                  {/* Password Strength Indicator */}
                  {password.length > 0 && (
                    <div className={`mt-2 pt-2 border-t border-border flex items-center justify-between`}>
                      <span className="text-xs">Strength:</span>
                      <span className={`text-xs font-medium ${getPasswordStrengthColor(passwordValidation.strength)}`}>
                        {getPasswordStrengthLabel(passwordValidation.strength)}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {!password && (
                <p className="text-xs">
                  Password must be at least 12 characters with uppercase, lowercase, number, special character, and cannot contain easily guessable patterns
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium">
                Confirm New Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pr-10"
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className={`text-xs flex items-center ${passwordsMatch ? 'text-green-500' : 'text-red-500'}`}>
                  <span className="mr-1">{passwordsMatch ? '✓' : '✗'}</span>
                  {passwordsMatch ? 'Passwords match' : 'Passwords do not match'}
                </div>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={resetPassword.isPending || !passwordValidation.isValid || !passwordsMatch}
            >
              {resetPassword.isPending ? "Resetting..." : "Reset Password"}
            </Button>
          </form>

          <div className="mt-6 pt-6 border-t border-border text-center">
            <Link to={ROUTES.LOGIN}>
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  //Password Reset Success
  function passwordResetSuccessBody() {
    return (
      <Card className="w-full max-w-md border shadow-sm">
        <CardHeader>
          <div className="mx-auto mb-4 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-500" />
          </div>
          <CardTitle className="text-2xl font-semibold">
            Password Reset Successfully
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p>
            Your password has been successfully reset. You can now log in with your new password.
          </p>
          <div className="pt-4">
            <Link to={ROUTES.LOGIN}>
              <Button className="w-full bg-primary hover:bg-primary/90">
                Continue to Login
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }
};

export default ResetPasswordPage;
